import {
  SolutionPuposesEnum,
  SolutionTypesEnum,
} from '../../models/selectOptionsModels';
import { TType } from '../../hooks/useTranslation';
import { INBOUND_FIELD, trimDate } from '../../store/projects/utils';
import { TransactionType } from '../../models/project';
import { ESTABLISHMENT_TYPE } from '../ProjectTypeSelect/utils';
import { getTotalInvestmentsSum } from '../../utils/utils';
import { calculateSum } from '../ProjectTabFinancials/utils';
import { numericFormatter } from 'react-number-format';
import { EUR } from '../../constants';

type LabelMap = Record<string, string>;

const asMap = (value: unknown): LabelMap => value as unknown as LabelMap;

const translateCode = (t: TType, namespace: string, code?: string): string =>
  code ? (asMap(t(namespace))[code] ?? code) : '';

const joinCodes = (
  codes: string[] | undefined,
  resolve: (code: string) => string,
): string => (codes ?? []).map(resolve).filter(Boolean).join(', ');

export const mapDirective = (
  project,
  t: TType,
  getEurRate: (fy?: string) => number | undefined = () => undefined,
  isEurCurrency = false,
) => {
  const directive = project?.directive;
  const purposes = asMap(t('solutionPuposes'));
  const solutionTypes = {
    ...asMap(t('solutionTypesGrid')),
    ...asMap(t('solutionTypes')),
  };

  const directiveFrozen = project.isep;
  const pick = <T>(directiveVal: T, projectVal: T): T =>
    directiveFrozen ? directiveVal : (projectVal ?? directiveVal);

  const investments = project?.investments ?? [];
  const liveLand = calculateSum(investments, 'BUDGET', 'landCost');
  const liveProperty = calculateSum(
    investments,
    'BUDGET',
    'propertyInvestment',
  );
  const liveRetailInv = calculateSum(investments, 'BUDGET', 'retailInvestment');
  const liveRetailCost = calculateSum(investments, 'BUDGET', 'retailCost');

  const propertyInvestment = pick(directive?.propertyInvestment, liveProperty);
  const retailInvestment = pick(directive?.retailInvestment, liveRetailInv);
  const retailCost = pick(directive?.retailCost, liveRetailCost);
  const totalLeaseAmount = pick(
    directive?.totalLeaseAmount,
    project?.totalLeaseAmount,
  );

  const includeLease = shouldExternalLeaseAppear(project);
  const totalProjectAmountInclExternalLease = pick(
    directive?.totalProjectAmountInclExternalLease,
    getTotalInvestmentsSum(
      Number(liveLand) || 0,
      Number(liveProperty) || 0,
      Number(liveRetailCost) || 0,
      Number(liveRetailInv) || 0,
    ) + (includeLease ? Number(project?.totalLeaseAmount) || 0 : 0),
  );

  const sumBudgetEur = (field: string): number =>
    investments.reduce((sum, item) => {
      const value = Number(item?.BUDGET?.[field]) || 0;
      return sum + value * (getEurRate(item?.fiscalYear) ?? 0);
    }, 0);

  const defaultRate = getEurRate(
    project?.financialSummaryInfos?.[0]?.fiscalYear,
  );

  const eurRatesAvailable = getEurRate() !== undefined;

  const liveLandEur = sumBudgetEur('landCost');
  const livePropertyEur = sumBudgetEur('propertyInvestment');
  const liveRetailInvEur = sumBudgetEur('retailInvestment');
  const liveRetailCostEur = sumBudgetEur('retailCost');
  const liveLeaseEur =
    (Number(project?.totalLeaseAmount) || 0) * (defaultRate ?? 0);
  const liveTotalEur =
    getTotalInvestmentsSum(
      Number(liveLandEur) || 0,
      Number(livePropertyEur) || 0,
      Number(liveRetailCostEur) || 0,
      Number(liveRetailInvEur) || 0,
    ) + (includeLease ? liveLeaseEur : 0);

  const resolveEur = (
    directiveVal?: number,
    liveVal?: number,
  ): number | undefined =>
    isEurCurrency
      ? undefined
      : pick(directiveVal, eurRatesAvailable ? liveVal : undefined);

  const propertyInvestmentEur = resolveEur(
    directive?.propertyInvestmentEur,
    livePropertyEur,
  );
  const retailInvestmentEur = resolveEur(
    directive?.retailInvestmentEur,
    liveRetailInvEur,
  );
  const retailCostEur = resolveEur(directive?.retailCostEur, liveRetailCostEur);
  const totalLeaseAmountEur = resolveEur(
    directive?.externalLeaseCommitmentEur,
    liveLeaseEur,
  );
  const totalProjectAmountInclExternalLeaseEur =
    resolveEur(
      directive?.totalProjectAmountInclExternalLeaseEur,
      liveTotalEur,
    ) || undefined;

  return {
    ...pick(directive, project),
    establishmentType: translateCode(
      t,
      'establishmentTypes',
      pick(directive?.establishmentType, project?.establishmentType),
    ),
    [INBOUND_FIELD]: pick(
      directive?.[INBOUND_FIELD],
      project?.stockedRangeAdjustment,
    ),
    siteType: translateCode(
      t,
      'siteTypes',
      pick(directive?.siteType, project?.siteType),
    ),
    transactionType: translateCode(
      t,
      'transactionTypes',
      pick(directive?.transactionType, project?.transactionType),
    ),
    solutionCategory: joinCodes(
      pick(
        directive?.solutionCategory,
        project?.solutions?.map((solution) => solution.solutionCategory),
      ),
      (code) => translateCode(t, 'solutionCategories', code),
    ),
    unitCategory: translateCode(
      t,
      'unitCategories',
      pick(directive?.unitCategory, project?.unitCategory),
    ),
    solutionPurpose: joinCodes(
      pick(
        directive?.solutionPurpose,
        project?.solutions?.map((solution) => solution.solutionPurpose),
      ),
      (code) => {
        const value =
          SolutionPuposesEnum[code as keyof typeof SolutionPuposesEnum] ?? code;
        return purposes[value] ?? code;
      },
    ),
    solutionType: joinCodes(
      pick(
        directive?.solutionType,
        project?.solutions?.map((solution) => solution.solutionType),
      ),
      (code) => {
        const value =
          SolutionTypesEnum[code as keyof typeof SolutionTypesEnum] ?? code;
        return solutionTypes[value] ?? code;
      },
    ),
    unitType: translateCode(
      t,
      'unitTypes',
      pick(directive?.unitType, project?.unitType),
    ),
    initialProjectStartDate: pick(
      directive?.initialProjectStartDate,
      trimDate(project?.projectStartDate) as string,
    ),
    initialProjectDeliveryDate: pick(
      directive?.initialProjectDeliveryDate,
      trimDate(project?.projectCompletionDate) as string,
    ),
    propertyInvestment,
    retailInvestment,
    retailCost,
    totalLeaseAmount,
    totalProjectAmountInclExternalLease,
    propertyInvestmentEur,
    retailInvestmentEur,
    retailCostEur,
    totalLeaseAmountEur,
    totalProjectAmountInclExternalLeaseEur,
  };
};

export const EMPTY = 'Missing information';

const withThousandsSeparator = (value: number): string =>
  numericFormatter(String(Math.round(value)), {
    thousandSeparator: ',',
    decimalScale: 0,
  });

export const formatMoneyWithEur = (
  localValue?: number,
  eurValue?: number,
  currencySymbol = '',
): string => {
  if (localValue === undefined || localValue === null) {
    return EMPTY;
  }
  const localAmount = withThousandsSeparator(localValue);
  const main = `${localAmount} K${currencySymbol}`;
  if (currencySymbol === EUR || eurValue === undefined || eurValue === null) {
    return main;
  }
  const eurAmount = withThousandsSeparator(eurValue);
  return `${main} | ${eurAmount}K${EUR}`;
};

export const shouldExternalLeaseAppear = (project) => {
  return (
    project?.transactionType === TransactionType.LEASE &&
    project?.establishmentType !== ESTABLISHMENT_TYPE.CLOSE &&
    (!project?.isLeasedFromAnotherIngkaCompany ||
      project?.isLeasedFromAnotherIngkaCompany === 'false')
  );
};
export const formatNumber = (value?: number): string =>
  value === undefined || value === null ? EMPTY : value.toLocaleString();

export const formatText = (value?: string): string =>
  value && value.length > 0 ? value : EMPTY;
type DirectiveTypeKey =
  | 'NEW'
  | 'NEW_MEETING_PLACE'
  | 'SISAN'
  | 'RELOCATION'
  | 'FORMAT_TRANSFORMATION'
  | 'CLOSE';

type ConditionalField = {
  field: string;
  unitCategories?: string[];
  solutionCategories?: string[];
  leaseCheck?: boolean;
  translationKey?: string;
  alternativeMissingLabel?: string;
};

type SectionField = string | ConditionalField;

export type ResolvedField = {
  field: string;
  translationKey: string | null;
  alternativeMissingLabel: string | null;
};

export const DIRECTIVE_FIELDS_MAP = {
  NEW: {
    general: [
      'projectName',
      'confidentiality',
      'referenceId',
      'countryName',
      'city',
      'unitCategory',
      'unitType',
      'establishmentType',
      'transactionType',
      'projectOwner',
    ],
    dates: ['initialProjectStartDate', 'initialProjectDeliveryDate'],
    dependencies: ['mergedWithReferenceId'],
    investment: [
      {
        field: 'propertyInvestment',
        translationKey: 'labels.propertyInvestmentCg0111',
      },
      { field: 'retailCost', translationKey: 'labels.retailCostsCg1516' },
      {
        field: 'retailInvestment',
        translationKey: 'labels.retailInvestmentCg1214',
      },
      { field: 'totalLeaseAmount', leaseCheck: true },
    ],
    replenishmentAndFulfilment: [
      { field: 'replenishmentUnit', unitCategories: ['CMP'] },
      {
        field: INBOUND_FIELD,
        unitCategories: ['CFU'],
        translationKey: 'ppm.columns.stockedRangeAdjustment',
      },
      { field: 'truckFulfilmentCapacityAdjustment', unitCategories: ['CFU'] },
      {
        field: 'parcelFulfilmentCapacityAdjustment',
        unitCategories: ['CFU'],
      },
    ],
  },
  NEW_MEETING_PLACE: {
    general: [
      'projectName',
      'confidentiality',
      'referenceId',
      'countryName',
      'city',
      'unitCategory',
      'unitType',
      'establishmentType',
      'transactionType',
      'siteType',
      'projectOwner',
    ],
    dates: ['initialProjectStartDate', 'initialProjectDeliveryDate'],
    dependencies: ['mergedWithReferenceId'],
    investment: [
      {
        field: 'propertyInvestment',
        translationKey: 'labels.propertyInvestmentCg0111',
      },
    ],
  },
  SISAN: {
    general: [
      'projectName',
      'confidentiality',
      'referenceId',
      'countryName',
      'city',
      'unitCategory',
      'unitType',
      'establishmentType',
      'transactionType',
      'solutionCategory',
      'solutionPurpose',
      'solutionType',
      'projectOwner',
    ],
    dates: ['initialProjectStartDate', 'initialProjectDeliveryDate'],
    dependencies: ['mergedWithReferenceId'],
    investment: [
      {
        field: 'propertyInvestment',
        translationKey: 'labels.propertyInvestmentCg0111',
      },
      { field: 'retailCost', translationKey: 'labels.retailCostsCg1516' },
      {
        field: 'retailInvestment',
        translationKey: 'labels.retailInvestmentCg1214',
      },
      { field: 'totalLeaseAmount', leaseCheck: true },
    ],
    replenishmentAndFulfilment: [
      {
        field: INBOUND_FIELD,
        solutionCategories: ['FULFILMENT_AND_REPLENISHMENT'],
        translationKey: 'ppm.columns.stockedRangeAdjustment',
      },
      {
        field: 'truckFulfilmentCapacityAdjustment',
        solutionCategories: ['FULFILMENT_AND_REPLENISHMENT'],
      },
      {
        field: 'parcelFulfilmentCapacityAdjustment',
        solutionCategories: ['FULFILMENT_AND_REPLENISHMENT'],
      },
    ],
  },
  RELOCATION: {
    general: [
      'projectName',
      'confidentiality',
      'referenceId',
      'countryName',
      'city',
      'unitCategory',
      'unitType',
      'establishmentType',
      'transactionType',
      'projectOwner',
    ],
    dates: ['initialProjectStartDate', 'initialProjectDeliveryDate'],
    dependencies: ['mergedWithReferenceId'],
    investment: [
      {
        field: 'propertyInvestment',
        translationKey: 'labels.propertyInvestmentCg0111',
      },
      { field: 'retailCost', translationKey: 'labels.retailCostsCg1516' },
      {
        field: 'retailInvestment',
        translationKey: 'labels.retailInvestmentCg1214',
      },
      { field: 'totalLeaseAmount', leaseCheck: true },
    ],
    replenishmentAndFulfilment: [
      {
        field: INBOUND_FIELD,
        unitCategories: ['CFU'],
        translationKey: 'ppm.columns.stockedRangeAdjustment',
      },
      { field: 'truckFulfilmentCapacityAdjustment', unitCategories: ['CFU'] },
      {
        field: 'parcelFulfilmentCapacityAdjustment',
        unitCategories: ['CFU'],
      },
    ],
  },
  FORMAT_TRANSFORMATION: {
    general: [
      'projectName',
      'confidentiality',
      'referenceId',
      'countryName',
      'city',
      'unitCategory',
      'unitType',
      'establishmentType',
      'transactionType',
      'projectOwner',
    ],
    dates: ['initialProjectStartDate', 'initialProjectDeliveryDate'],
    dependencies: ['mergedWithReferenceId'],
    investment: [
      {
        field: 'propertyInvestment',
        translationKey: 'labels.propertyInvestmentCg0111',
      },
      { field: 'retailCost', translationKey: 'labels.retailCostsCg1516' },
      {
        field: 'retailInvestment',
        translationKey: 'labels.retailInvestmentCg1214',
      },
      { field: 'totalLeaseAmount', leaseCheck: true },
    ],
    replenishmentAndFulfilment: [
      {
        field: INBOUND_FIELD,
        unitCategories: ['CMP', 'CFU'],
        translationKey: 'ppm.columns.stockedRangeAdjustment',
      },
      {
        field: 'truckFulfilmentCapacityAdjustment',
        unitCategories: ['CMP', 'CFU'],
      },
      {
        field: 'parcelFulfilmentCapacityAdjustment',
        unitCategories: ['CMP', 'CFU'],
      },
    ],
  },
  CLOSE: {
    general: [
      'projectName',
      'confidentiality',
      'referenceId',
      'countryName',
      'city',
      'unitCategory',
      'unitType',
      'establishmentType',
      'siteType',
      'projectOwner',
    ],
    dates: ['initialProjectStartDate', 'initialProjectDeliveryDate'],
    dependencies: ['mergedWithReferenceId'],
    investment: [
      {
        field: 'propertyInvestment',
        translationKey: 'labels.propertyInvestmentCg0111',
      },
      { field: 'retailCost', translationKey: 'labels.retailCostsCg1516' },
      {
        field: 'retailInvestment',
        translationKey: 'labels.retailInvestmentCg1214',
      },
      { field: 'totalLeaseAmount', leaseCheck: true },
    ],
    replenishmentAndFulfilment: [
      'truckFulfilmentCapacityAdjustment',
      'parcelFulfilmentCapacityAdjustment',
    ],
    people: ['totalNumberOfFullTimeEmployees', 'totalNumberOfEmployees'],
  },
};

const resolveFields = (
  fields: SectionField[],
  unitCategory: string | undefined,
  solutionCategories: string[] | undefined,
  showLeaseAmount: boolean,
): ResolvedField[] =>
  fields.flatMap((f) => {
    if (typeof f === 'string')
      return [
        { field: f, translationKey: null, alternativeMissingLabel: null },
      ];
    if (f.leaseCheck && !showLeaseAmount) return [];
    if (f.unitCategories && !f.unitCategories.includes(unitCategory)) return [];
    if (
      f.solutionCategories &&
      !f.solutionCategories.some((c) => solutionCategories?.includes(c))
    )
      return [];
    return [
      {
        field: f.field,
        translationKey: f.translationKey ?? null,
        alternativeMissingLabel: f.alternativeMissingLabel ?? null,
      },
    ];
  });

export const getDirectiveFields = (project) => {
  const establishmentType =
    project?.directive?.establishmentType ?? project?.establishmentType;
  const unitCategory =
    project?.directive?.unitCategory ?? project?.unitCategory;
  const solutionCategories: string[] | undefined =
    project?.directive?.solutionCategory ?? project?.solutionCategory;
  const showLeaseAmount = shouldExternalLeaseAppear(project);

  let typeKey: DirectiveTypeKey;
  if (establishmentType === 'NEW' && unitCategory === 'meetingPlace') {
    typeKey = 'NEW_MEETING_PLACE';
  } else {
    typeKey = establishmentType as DirectiveTypeKey;
  }

  const rawMap = DIRECTIVE_FIELDS_MAP[typeKey];
  if (!rawMap) return {};

  return Object.fromEntries(
    Object.entries(rawMap).map(([section, fields]) => [
      section,
      resolveFields(
        fields as SectionField[],
        unitCategory,
        solutionCategories,
        showLeaseAmount,
      ),
    ]),
  );
};

export const FIELD_TO_TAB: Record<string, { tab: string; fieldName: string }> =
  {
    projectName: { tab: 'Project', fieldName: 'projectName' },
    confidentiality: { tab: 'Project', fieldName: 'confidential' },
    mergedWithReferenceId: {
      tab: 'Project',
      fieldName: 'mergedWithReferenceId',
    },
    initialProjectStartDate: {
      tab: 'Project',
      fieldName: 'targetProjectStartDate',
    },
    initialProjectDeliveryDate: {
      tab: 'Project',
      fieldName: 'targetProjectEndDate',
    },
    countryName: { tab: 'Site/Unit', fieldName: 'countryCode' },
    city: { tab: 'Site/Unit', fieldName: 'city' },
    unitCategory: { tab: 'Site/Unit', fieldName: 'unitCategory' },
    unitType: { tab: 'Site/Unit', fieldName: 'unitType' },
    establishmentType: { tab: 'Site/Unit', fieldName: '' },
    transactionType: { tab: 'Site/Unit', fieldName: 'transactionType' },
    siteType: { tab: 'Site/Unit', fieldName: 'siteType' },
    solutionCategory: { tab: 'Site/Unit', fieldName: 'solutionCategory' },
    solutionPurpose: { tab: 'Site/Unit', fieldName: 'solutionPurpose' },
    solutionType: { tab: 'Site/Unit', fieldName: 'solutionType' },
    projectOwner: { tab: 'Responsible', fieldName: 'projectOwner' },
    propertyInvestment: { tab: 'Financials', fieldName: 'investments' },
    retailInvestment: { tab: 'Financials', fieldName: 'investments' },
    retailCost: { tab: 'Financials', fieldName: 'investments' },
    totalLeaseAmount: { tab: 'Site/Unit', fieldName: 'totalLeaseAmount' },
    [INBOUND_FIELD]: {
      tab: 'Capacity',
      fieldName: 'stockedRangeAdjustment',
    },
    truckFulfilmentCapacityAdjustment: {
      tab: 'Capacity',
      fieldName: 'truckFulfilmentCapacityAdjustment',
    },
    parcelFulfilmentCapacityAdjustment: {
      tab: 'Capacity',
      fieldName: 'parcelFulfilmentCapacityAdjustment',
    },
    totalNumberOfFullTimeEmployees: {
      tab: 'Project',
      fieldName: 'totalNumberOfFullTimeEmployees',
    },
    totalNumberOfEmployees: {
      tab: 'Project',
      fieldName: 'totalNumberOfEmployees',
    },
    replenishmentUnit: {
      tab: 'Capacity',
      fieldName: 'replenishmentUnit',
    },
    totalProjectAmountInclExternalLease: {
      tab: 'Financials',
      fieldName: 'investments',
    },
  };
